import {
  importFields,
  type ListParams,
  labels,
  MAX_PAGE_SIZE,
  type RefMap,
  type ResolvedField,
  type Resource,
  templateHeader,
} from '@tnn/core'
import { type Rec, resourceApi } from '../api/resources'
import { type FieldValueContext, fieldText } from './FieldValue'

/* ------------------------------------------------------------------ */
/* Export                                                              */
/* ------------------------------------------------------------------ */

/** Pages through the API so exports include every row, not just the visible page. */
export async function fetchAll(resource: Resource, params: Partial<ListParams>, onProgress?: (done: number, total: number) => void) {
  const items: Rec[] = []
  const refs: RefMap = {}
  let page = 1
  let total = Number.POSITIVE_INFINITY
  while (items.length < total) {
    const res = await resourceApi(resource.key).list({ ...params, page, pageSize: MAX_PAGE_SIZE })
    items.push(...res.items)
    for (const [key, map] of Object.entries(res.refs)) refs[key] = { ...(refs[key] ?? {}), ...map }
    total = res.total
    onProgress?.(items.length, total)
    if (res.items.length === 0) break
    page += 1
  }
  return { items, refs }
}

export function exportFields(resource: Resource, visible?: string[]): ResolvedField[] {
  const names = visible?.length ? visible : resource.columns
  return names.map((n) => resource.field(n)).filter((f): f is ResolvedField => !!f && f.export && f.type !== 'json')
}

function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = Object.assign(document.createElement('a'), { href: url, download: fileName })
  document.body.append(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const NUMERIC = new Set(['number', 'integer', 'money', 'percent'])

export async function exportRecords(opts: {
  resource: Resource
  fields: ResolvedField[]
  records: Rec[]
  ctx: FieldValueContext
  format: 'xlsx' | 'csv'
  fileName: string
}) {
  const { fields, records, ctx, format, fileName } = opts
  const header = (f: ResolvedField) => ctx.t(f.label)

  if (format === 'csv') {
    const { default: Papa } = await import('papaparse')
    const rows = records.map((r) =>
      Object.fromEntries(fields.map((f) => [header(f), NUMERIC.has(f.type) ? (r[f.name] ?? '') : fieldText(f, r[f.name], ctx)])),
    )
    // The BOM makes Excel open Thai text as UTF-8
    download(new Blob([`﻿${Papa.unparse(rows)}`], { type: 'text/csv;charset=utf-8' }), `${fileName}.csv`)
    return
  }

  // Spreadsheet libraries load only when someone exports or imports
  const { default: writeXlsxFile } = await import('write-excel-file/browser')
  const file = await writeXlsxFile(records, {
    sheet: ctx.t(labels.plural(ctx.resource)).slice(0, 31),
    stickyRowsCount: 1,
    columns: fields.map((f) => ({
      width: f.type === 'textarea' ? 40 : NUMERIC.has(f.type) ? 14 : 22,
      header: { value: header(f), fontWeight: 'bold', backgroundColor: '#EEF1F0' },
      cell: (r: Rec) => {
        const v = r[f.name]
        if (v == null || v === '') return null
        if (NUMERIC.has(f.type)) return { value: Number(v), type: Number, format: f.type === 'money' ? '#,##0.00' : '#,##0.##' }
        if (f.type === 'date') return { value: new Date(`${v}T00:00:00`), type: Date, format: 'dd/mm/yyyy' }
        if (f.type === 'datetime') return { value: new Date(String(v)), type: Date, format: 'dd/mm/yyyy hh:mm' }
        return { value: fieldText(f, v, ctx), type: String }
      },
    })),
  })
  download(await file.toBlob(), `${fileName}.xlsx`)
}

export interface SheetColumn<T> {
  header: string
  value: (row: T) => string | number | null | undefined
  type?: 'text' | 'money' | 'number' | 'date'
  width?: number
}

/** Rows as a plain .xlsx sheet with the columns given (a bank's transfer list, a filing for the authorities) */
export async function exportSheet<T>(opts: { fileName: string; sheet: string; columns: SheetColumn<T>[]; rows: T[] }) {
  const { default: writeXlsxFile } = await import('write-excel-file/browser')
  const file = await writeXlsxFile(opts.rows, {
    sheet: opts.sheet.slice(0, 31),
    stickyRowsCount: 1,
    columns: opts.columns.map((c) => ({
      width: c.width ?? (c.type === 'money' || c.type === 'number' ? 14 : 22),
      header: { value: c.header, fontWeight: 'bold', backgroundColor: '#EEF1F0' },
      cell: (row: T) => {
        const v = c.value(row)
        if (v == null || v === '') return null
        if (c.type === 'money' || c.type === 'number')
          return { value: Number(v), type: Number, format: c.type === 'money' ? '#,##0.00' : '#,##0.##' }
        if (c.type === 'date') return { value: new Date(`${v}T00:00:00`), type: Date, format: 'dd/mm/yyyy' }
        return { value: String(v), type: String }
      },
    })),
  })
  download(await file.toBlob(), `${opts.fileName}.xlsx`)
}

/** An empty file with the right headers, so people know what to fill in. */
export async function downloadTemplate(resource: Resource, ctx: FieldValueContext, fileName: string) {
  const fields = importFields(resource)
  const { default: writeXlsxFile } = await import('write-excel-file/browser')
  const file = await writeXlsxFile([{}], {
    columns: fields.map((f) => ({ width: 22, header: { value: ctx.t(templateHeader(f)), fontWeight: 'bold' }, cell: () => null })),
  })
  download(await file.toBlob(), `${fileName}.xlsx`)
}

/* ------------------------------------------------------------------ */
/* Import                                                              */
/* ------------------------------------------------------------------ */

export interface ParsedFile {
  headers: string[]
  rows: unknown[][]
}

/** Reads .xlsx or .csv into a header row and data rows. */
export async function parseFile(file: File): Promise<ParsedFile> {
  const name = file.name.toLowerCase()
  let table: unknown[][]
  if (name.endsWith('.csv') || file.type === 'text/csv') {
    const { default: Papa } = await import('papaparse')
    const text = await file.text()
    table = Papa.parse<unknown[]>(text.replace(/^﻿/, ''), { skipEmptyLines: 'greedy' }).data
  } else {
    const { readSheet } = await import('read-excel-file/browser')
    table = (await readSheet(file)) as unknown[][]
  }
  const [head = [], ...rows] = table
  return {
    headers: head.map((h) => String(h ?? '').trim()),
    rows: rows.filter((r) => r.some((c) => c !== null && c !== '')),
  }
}

const normalize = (s: string) => s.toLowerCase().replace(/[\s_\-().]/g, '')

/** Guesses which field each file column belongs to, by label, alias or name. */
export function autoMap(headers: string[], fields: ResolvedField[], label: (f: ResolvedField) => string): (string | null)[] {
  const used = new Set<string>()
  return headers.map((h) => {
    const n = normalize(h)
    const match = fields.find((f) => {
      if (used.has(f.name)) return false
      const candidates = [f.name, label(f), ...(f.import ? (f.import.aliases ?? []) : [])]
      const header = f.import ? f.import.header : undefined
      if (header) candidates.push(label({ ...f, label: header }))
      return candidates.some((c) => normalize(c) === n)
    })
    if (match) used.add(match.name)
    return match?.name ?? null
  })
}

function cellValue(field: ResolvedField, raw: unknown): unknown {
  if (raw == null || raw === '') return undefined
  if (raw instanceof Date) {
    // A time cell (06:00) is a date in 1899 to Excel, read at UTC midnight plus the time
    if (field.type === 'time') return raw.toISOString().slice(11, 16)
    const iso = new Date(raw.getTime() - raw.getTimezoneOffset() * 60_000).toISOString()
    return field.type === 'datetime' ? raw.toISOString() : iso.slice(0, 10)
  }
  if (NUMERIC.has(field.type)) {
    const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[,\s฿]/g, ''))
    return Number.isFinite(n) ? n : raw
  }
  if (field.type === 'boolean') return ['1', 'true', 'yes', 'ใช่', 'y'].includes(String(raw).trim().toLowerCase())
  if (field.type === 'tags' || field.type === 'multiselect')
    return String(raw)
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean)
  return String(raw).trim()
}

/** Turns file rows into field-keyed objects for the import endpoint. */
export function buildRows(file: ParsedFile, mapping: (string | null)[], fields: ResolvedField[]): Record<string, unknown>[] {
  const byName = new Map(fields.map((f) => [f.name, f]))
  return file.rows.map((row) => {
    const out: Record<string, unknown> = {}
    mapping.forEach((name, i) => {
      const field = name ? byName.get(name) : undefined
      if (!field) return
      const value = cellValue(field, row[i])
      if (value !== undefined) out[field.name] = value
    })
    return out
  })
}
